import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import type {
  BrazilianUF,
  CreatePriceTableFormData,
} from '@/schemas/commercial/price-table.schema';

export function useBillingConditions(gi?: number) {
  const { control } = useFormContext<CreatePriceTableFormData>();

  const {
    fields: groupFields,
    append: appendGroup,
    remove: removeGroup,
  } = useFieldArray({
    control,
    name: 'groups',
    keyName: 'rhfId',
  });

  const {
    fields: condFields,
    append: appendCond,
    remove: removeCond,
  } = useFieldArray({
    control,
    name: gi !== undefined ? `groups.${gi}.conditions` : 'groups.0.conditions',
    keyName: 'rhfId',
  });

  // Watch all groups (to block already used UFs)
  const allGroups = useWatch({
    control,
    name: 'groups',
  }) as Array<{
    id: string;
    priority?: boolean;
    conditions: Array<{
      id: string;
      salesFor?: BrazilianUF[];
      toBillFor?: string;
      billingLimitInCents?: string;
      icmsContributor?: boolean | null;
    }>;
  }>;

  // UFs used by OTHER groups
  const usedInOtherGroups = new Set<string>();
  (allGroups ?? []).forEach((g, idx) => {
    if (gi !== undefined && idx === gi) return;
    g?.conditions?.forEach((c) => {
      (c?.salesFor ?? []).forEach((uf) => {
        usedInOtherGroups.add(uf);
      });
    });
  });

  // Total conditions across the whole table (deletion is only allowed above 1)
  const totalConditions = (allGroups ?? []).reduce(
    (acc, g) => acc + (g?.conditions?.length ?? 0),
    0,
  );

  // Atomic removal decided at hook level: deletes the clicked condition, or
  // removes the whole group when it is the group's last condition (a group is
  // never left empty). Always keeps at least one group with one condition.
  const removeCondition = (gi: number, ci: number) => {
    if (totalConditions <= 1) return;
    if ((allGroups?.[gi]?.conditions?.length ?? 0) <= 1) {
      removeGroup(gi);
      return;
    }
    removeCond(ci);
  };

  // Helpers for UF Combobox
  // Only blocks UFs already used by other groups.
  // Keeps the ones already selected in the current condition.
  const getUfOptionsFor = (ufList: { id: string; text: string }[], currentValue?: string[]) => {
    const current = new Set(currentValue ?? []);
    return ufList.filter((opt) => !usedInOtherGroups.has(opt.id) || current.has(opt.id));
  };

  const isUfDisabled = (ufId: string, currentValue?: string[]) => {
    const current = new Set(currentValue ?? []);
    return usedInOtherGroups.has(ufId) && !current.has(ufId);
  };

  return {
    groupFields,
    appendGroup,
    removeGroup,
    condFields,
    appendCond,
    removeCond,

    control,
    getUfOptionsFor,
    isUfDisabled,
    totalConditions,
    removeCondition,
  };
}
